import { DotsSixVerticalIcon, XIcon } from '@phosphor-icons/react';
import { useViewport } from '@xyflow/react';
import { useGT } from 'gt-react';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  getStaticDefaultChipMode,
  getStaticDefaultIndicator,
  type StaticDefaultChipMode,
} from './static-default-chip-mode';

/** Resting position of the chip relative to the socket handle, before any drag. */
const BASE_OFFSET = { x: -24, y: 0 };

/** Input width in `ch` units that hugs the draft's length, within sane bounds. */
function clampInputWidthCh(charCount: number): number {
  const MIN_WIDTH_CH = 3;
  const MAX_WIDTH_CH = 30;
  return Math.min(Math.max(charCount + 1, MIN_WIDTH_CH), MAX_WIDTH_CH);
}

function formatStaticValuePreview(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function computeDraftFromValue(
  mode: StaticDefaultChipMode,
  value: unknown,
  preview: string,
): string {
  if (mode === 'number') {
    return typeof value === 'number' ? String(value) : '';
  }
  return typeof value === 'string' ? value : preview;
}

/** Vector (in flow px) from the anchor handle to the chip's current position. */
function useChipOffset() {
  const { zoom } = useViewport();
  const [offset, setOffset] = useState({ x: BASE_OFFSET.x, y: BASE_OFFSET.y });
  const dragStateRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const onDragPointerDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStateRef.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      originX: offset.x,
      originY: offset.y,
    };
  };

  const onDragPointerMove = (e: React.PointerEvent) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    e.stopPropagation();
    const dx = (e.clientX - drag.startClientX) / zoom;
    const dy = (e.clientY - drag.startClientY) / zoom;
    setOffset({ x: drag.originX + dx, y: drag.originY + dy });
  };

  const endDrag = (e: React.PointerEvent) => {
    if (dragStateRef.current?.pointerId !== e.pointerId) return;
    dragStateRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  return { offset, onDragPointerDown, onDragPointerMove, endDrag };
}

export function StaticDefaultChip({
  socketId,
  socketName,
  socketType,
  value,
  editable,
  onChange,
  onClear,
}: {
  socketId: string;
  socketName: string;
  socketType: string | undefined;
  value: unknown;
  editable: boolean;
  onChange: (socketId: string, next: unknown) => void;
  onClear: (socketId: string) => void;
}) {
  const gt = useGT();
  const mode = getStaticDefaultChipMode(socketType, value);
  const indicator = getStaticDefaultIndicator(socketType, value);
  const preview = formatStaticValuePreview(value);
  const { offset, onDragPointerDown, onDragPointerMove, endDrag } =
    useChipOffset();

  const [draft, setDraft] = useState(() =>
    computeDraftFromValue(mode, value, preview),
  );

  // Keep draft in sync when the side panel (or another chip) updates the value —
  // computed during render (not in an effect) to avoid an extra commit/cascading
  // render; see https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [prevMode, setPrevMode] = useState(mode);
  const [prevValue, setPrevValue] = useState(value);
  if (mode !== prevMode || value !== prevValue) {
    setPrevMode(mode);
    setPrevValue(value);
    setDraft(computeDraftFromValue(mode, value, preview));
  }

  function commitStringDraft() {
    if (mode === 'number') {
      const trimmed = draft.trim();
      if (trimmed.length === 0) {
        onClear(socketId);
        return;
      }
      const parsed = Number(trimmed);
      if (Number.isFinite(parsed)) {
        onChange(socketId, parsed);
      }
      return;
    }

    if (draft === '') {
      onClear(socketId);
      return;
    }
    onChange(socketId, draft);
  }

  const lineLength = Math.hypot(offset.x, offset.y);
  const lineAngleDeg = (Math.atan2(offset.y, offset.x) * 180) / Math.PI;

  return (
    // Anchored exactly at the socket handle; children are positioned via
    // `offset` so the wire + box can be dragged away and still stay connected.
    <div
      // Anchored at the same point as the socket Handle (`!-left-3 !top-1/2`).
      className="absolute top-1/2 -left-3 z-20"
      // Stop React Flow from treating chip interactions as canvas pan/select noise.
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Connecting wire — stretches/rotates to track the box as it's dragged */}
      <div
        aria-hidden
        className="absolute top-0 left-0 h-px origin-left bg-muted-foreground/40"
        style={{
          width: lineLength,
          transform: `rotate(${lineAngleDeg}deg)`,
        }}
      />
      <div
        className="nodrag nopan absolute top-0 left-0 flex w-fit items-center gap-0.5 rounded border border-muted-foreground/30 bg-background px-1 py-0.5 shadow-sm"
        style={{
          // translateX(-100%) anchors the box by its right edge (not its
          // top-left corner) so the whole chip renders to the left of the
          // socket instead of extending back over the node.
          transform: `translate(${offset.x}px, ${offset.y}px) translateY(-50%) translateX(-100%)`,
        }}
      >
        {editable && (
          <button
            type="button"
            className="flex h-4 w-3 shrink-0 cursor-grab items-center justify-center text-muted-foreground/60 hover:text-muted-foreground active:cursor-grabbing"
            aria-label={gt('Drag to reposition default value for {name}', {
              name: socketName,
            })}
            onPointerDown={onDragPointerDown}
            onPointerMove={onDragPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            <DotsSixVerticalIcon className="h-2.5 w-2.5" />
          </button>
        )}
        {mode === 'boolean' ? (
          <div className="flex items-center gap-1 px-0.5">
            <Switch
              checked={value === true}
              disabled={!editable}
              onCheckedChange={(checked) => onChange(socketId, checked)}
              className="scale-75 origin-left"
              aria-label={gt('Default value for {name}', { name: socketName })}
            />
          </div>
        ) : mode === 'indicator' ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="cursor-default px-1 font-mono text-[10px] leading-none text-muted-foreground"
                title={preview}
              >
                {indicator}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs break-all font-mono text-xs">
              {preview.length > 0 ? preview : indicator}
            </TooltipContent>
          </Tooltip>
        ) : (
          <Input
            value={draft}
            type={mode === 'number' ? 'number' : 'text'}
            disabled={!editable}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitStringDraft}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur();
              }
              if (e.key === 'Escape') {
                setDraft(computeDraftFromValue(mode, value, preview));
                e.currentTarget.blur();
              }
            }}
            className="h-5 shrink-0 border-0 bg-transparent px-1 text-[10px] shadow-none focus-visible:ring-0"
            style={{ width: `${clampInputWidthCh(draft.length)}ch` }}
            aria-label={gt('Default value for {name}', { name: socketName })}
          />
        )}
        {editable && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-4 w-4 shrink-0 p-0 text-muted-foreground hover:text-destructive"
            aria-label={gt('Remove default value for {name}', {
              name: socketName,
            })}
            onClick={() => onClear(socketId)}
          >
            <XIcon className="h-2.5 w-2.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
